import React from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import NoData from "../../../shared/components/common/ui/NoData";
import { CampaignById } from "../../../services/api/Campaigns/CampaignById";
import { CampaignWizard } from "../components/wizard/CampaignWizard";
import { useCampaignsData } from "../hooks/useCampaignsData";

export default function CampaignEditPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { campaigns, loading, isFetching } = useCampaignsData();
  const [campaignDetails, setCampaignDetails] = React.useState(null);
  const [detailsLoading, setDetailsLoading] = React.useState(true);

  const fallbackCampaign = React.useMemo(() => {
    const campaignFromStore =
      campaigns.find((item) => String(item.id) === String(id)) || null;

    if (campaignFromStore) {
      return campaignFromStore;
    }

    if (String(location.state?.campaign?.id) === String(id)) {
      return location.state.campaign;
    }

    return null;
  }, [campaigns, id, location.state]);

  React.useEffect(() => {
    let isMounted = true;

    const loadCampaignDetails = async () => {
      if (!id) return;

      try {
        setDetailsLoading(true);
        const response = await CampaignById(id);

        if (!isMounted) return;

        setCampaignDetails(response?.campaign || null);
      } catch (error) {
        if (!isMounted) return;

        console.error("Erro ao carregar campanha para edição:", error);
        setCampaignDetails(null);
      } finally {
        if (isMounted) {
          setDetailsLoading(false);
        }
      }
    };

    loadCampaignDetails();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const campaign = React.useMemo(() => {
    if (!campaignDetails && !fallbackCampaign) {
      return null;
    }

    return {
      ...fallbackCampaign,
      ...campaignDetails,
      id: campaignDetails?.code || fallbackCampaign?.id || id,
    };
  }, [campaignDetails, fallbackCampaign, id]);

  if ((loading || isFetching || detailsLoading) && !campaign) {
    return (
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          width: "100%",
        }}
      >
        <Stack
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: 320 }}
        >
          <CircularProgress />
          <Typography color="text.secondary">
            Carregando dados da campanha...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          width: "100%",
        }}
      >
        <NoData
          title="Campanha não encontrada"
          description="Não foi possível localizar a campanha para edição."
          buttonText="Voltar para campanhas"
          onClick={() => navigate("/campanhas")}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        width: "100%",
      }}
    >
      <CampaignWizard mode="edit" campaign={campaign} />
    </Box>
  );
}
